import { IProjectMetadata } from '@/interfaces/report';
import {
  IAgencyLineItemImperial,
  ILccaActivityImperial,
  ILccaAlternativeImperial,
  ILccaCalculatedImperial,
} from '@/modules/lcca/interfaces/lccaCalculated';
import { BaseScenarioType } from '@/lib/types/IBaseScenario';
import { UnitSystem } from '@/interfaces/units';
import { BaseProbVariables } from '@/modules/lcca/interfaces/probabilistic';
import { urbanWeekday } from '@/modules/lcca/lib/trafficDsitributions';
import { getLccaFromR23Report } from '@/modules/lcca/lib/r23ToSuiteTransformers';
import { testReportMetadata } from '@/lib/data/exampleReportMetadata';
import { exampleAsphaltDesignR23Report } from '@/lib/data/exampleR23Reports';
import { DistributionTypes, IDistribution } from '@/lib/monteCarlo/interfaces';
import {userStore} from "@/store/store";

export const createNewFullLcca = function (project: IProjectMetadata) {
  const lcca = getLccaFromR23Report(testReportMetadata, exampleAsphaltDesignR23Report, -1);
  lcca.project = project.id;
  lcca.maxQueueLength_mi = 0;
  lcca.probabilistic = (function () {
    const probVariables: IDistribution[] = [];

    Object.keys(BaseProbVariables).forEach((varName) => {
      probVariables.push(<IDistribution>{
        id: null,
        name: varName,
        prob: DistributionTypes.deterministic,
        min: null,
        max: null,
        mean: 0,
        mode: null,
        std: null,
        shape: null,
        location: null,
      });
    });

    return probVariables;
  }());

  return lcca;
};

export const createNewLcca = function (project: IProjectMetadata): ILccaCalculatedImperial {
  const ds: ILccaCalculatedImperial = {
    id: -1,
    user: userStore.user ? userStore.user.id : null,
    project: project.id,
    created: new Date(Date.now()).toISOString(),
    modified: new Date(Date.now()).toISOString(),
    visible: true,
    displayUnits: project.displayUnits,
    unitSystem: UnitSystem.Imperial,
    scenarioType: BaseScenarioType.lcca,
    isComputedFromLegacy: false,
    state: project.state,


    name: 'New LCCA Scenario',

    // Scenario Information

    analysisDirection: null, // Freeform, mostly unused
    analyzedBy: null,
    projectExtent_begin: null, // Basically unused
    projectExtent_end: null, // ditto
    comments: null,

    maxQueueLength: null,
    // probabilistic: report.probabilistic ? report.probabilistic : [],

    // Analysis Options
    analysisPeriodLength_yrs: 50, // analysisPeriod
    analysisPeriodStart_yr: new Date(Date.now()).getFullYear(), // beginningAnalysisPeriod -> sometimes used improperly in legacy
    userCostComputationMethod: 'Calculated',
    userCostsInAnalysis: true,

    discountRate: 0.01,

    // Traffic Data
    trafficYear: null,

    annualGrowthTraffic: 0,
    singleTruckAadtPct: null, // singleTruckAADT
    combinationTruckAadtPct: null, // combinationTruckAADT
    numLanesNormal: null,

    aadtAnalysisPeriod: null,
    freeFlowCapacity: null, // vphpl
    speedLimitNormal_mph: null,
    maxQueueLength_mi: null,
    noShowPct: null,
    detourPct: null,
    additionalDetourTime_min: null,

    // maxQueueLength: report.maxQueueLength ? Number(report.maxQueueLength) : 15,

    // Traffic Hourly Distribution
    trafficHourlyDistribution: urbanWeekday,
    patternType: 'Urban - Weekday',

    probabilistic: (function () {
      const probVariables: IDistribution[] = [];

      Object.keys(BaseProbVariables).forEach((varName) => {
        probVariables.push(<IDistribution>{
          id: null,
          name: varName,
          prob: DistributionTypes.deterministic,
          min: null,
          max: null,
          mean: 0,
          mode: null,
          std: null,
          location: null,
          shape: null,
        });
      });

      return probVariables;
    }()),

    passengerCarsTime: 0,
    combinationTrucksTime: 0,
    singleUnitTrucksTime: 0,

    alternatives: [],
  };
  // ds.alternatives.forEach(A => console.log(A.index, A.name))

  return ds;
};

export const createNewLineItem = function () {
  const pxL: IAgencyLineItemImperial = {
    id: null,
    unitSystem: UnitSystem.Imperial,
    name: null,
    units: null,
    unitDescr: null,
    unitCost: 0,
    quantity: 0,
  };
  return pxL;
};

export const createNewActivity = function (lastActivityIndex: number = 0, alternativeIndex: number | null = 0) {
  const newActivityIndex = lastActivityIndex + 1;

  const pxActivity: ILccaActivityImperial = {

    id: null,
    unitSystem: UnitSystem.Imperial,
    name: 'New Activity',
    description: null,
    index: newActivityIndex,
    alternative: alternativeIndex,
    startYear: 0,
    serviceLife_yrs: 10,
    structuralLife_yrs: 50,
    agencyConstructionCost_thousands: null,
    agencyMaintenanceCost_thousands: null,

    userCostEnabled: false,
    workzoneLength_mi: null,
    workzoneCapacity: null,
    workzoneNumLanes: null,
    workzoneSpeedLimit_mph: null,
    workzoneDuration_days: null,

    userWorkzoneCost_thousands: null,

    laneClosePeriods: [],
    nightShift: false,

    lineItems: [],
  };
  return pxActivity;
};


export const createNewAlternative = function (lastIndex: number = 0) {
  const newAlternativeIndex = lastIndex + 1;

  const na: ILccaAlternativeImperial = {
    id: null,
    name: 'New LCCA Alternative',
    index: newAlternativeIndex,
    unitSystem: UnitSystem.Imperial,
    activities: [createNewActivity(0, newAlternativeIndex)],
  };

  return na;
};
